/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { nd } from "./tokens"

/**
 * A white, rounded content card. Cards stack in a page's main or aside column; split a card into
 * ruled-off parts with `Card.Section`.
 */
export function Card({
  title,
  badge,
  actions,
  children,
  padded = true,
  className,
}: {
  title?: React.ReactNode
  /** Shown next to the title, e.g. a count or status Badge */
  badge?: React.ReactNode
  /** Shown at the right of the header, e.g. a plain Button */
  actions?: React.ReactNode
  children?: React.ReactNode
  /** Set false for edge-to-edge content such as tables or row lists */
  padded?: boolean
  className?: string
}) {
  const hasHeader = title || badge || actions
  return (
    <section
      className={className}
      css={css`
        background: ${nd.color.surface};
        border-radius: ${nd.radius.large}px;
        box-shadow: ${nd.shadow.card};
        min-width: 0;
        /* round whatever touches the corners rather than clipping with overflow: hidden, which
           would cut off dropdown menus opened inside the card */
        & > :first-child {
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
        }
        & > :last-child {
          border-bottom-left-radius: inherit;
          border-bottom-right-radius: inherit;
        }
      `}
    >
      {hasHeader && (
        <header
          css={css`
            display: flex;
            align-items: center;
            gap: ${nd.space.sm}px;
            padding: ${nd.space.lg}px ${nd.space.lg}px ${padded ? 0 : nd.space.md}px;
            min-height: 20px;
          `}
        >
          {title && (
            <h2
              css={css`
                margin: 0;
                font-size: ${nd.fontSize.heading}px;
                font-weight: ${nd.fontWeight.semibold};
                color: ${nd.color.text};
                line-height: 20px;
              `}
            >
              {title}
            </h2>
          )}
          {badge}
          <div
            css={css`
              flex: 1;
            `}
          />
          {actions}
        </header>
      )}
      {padded ? (
        <div
          css={css`
            padding: ${hasHeader ? nd.space.md : nd.space.lg}px ${nd.space.lg}px ${nd.space.lg}px;
          `}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  )
}

/** A ruled-off part of a Card with its own optional subheading. */
function CardSection({
  title,
  subdued,
  children,
}: {
  title?: React.ReactNode
  /** Grey background, e.g. for totals or a footer */
  subdued?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      css={css`
        padding: ${nd.space.md}px ${nd.space.lg}px;
        border-top: 1px solid ${nd.color.border};
        background: ${subdued ? nd.color.surfaceSubdued : "transparent"};
      `}
    >
      {title && (
        <h3
          css={css`
            margin: 0 0 ${nd.space.sm}px;
            font-size: ${nd.fontSize.body}px;
            font-weight: ${nd.fontWeight.semibold};
            color: ${nd.color.text};
          `}
        >
          {title}
        </h3>
      )}
      {children}
    </div>
  )
}

Card.Section = CardSection

/**
 * A grey, rounded panel nested inside a card for secondary content attached to something in it,
 * e.g. an offer to order a line from another branch. Optional title and actions on one row.
 */
function CardInset({
  title,
  actions,
  children,
  padded = true,
}: {
  title?: React.ReactNode
  actions?: React.ReactNode
  children?: React.ReactNode
  /** Set false for a table that runs edge to edge */
  padded?: boolean
}) {
  return (
    <div
      css={css`
        border-radius: ${nd.radius.medium}px;
        background: ${nd.color.surfaceSubdued};
        box-shadow: inset 0 0 0 1px ${nd.color.border};
        overflow: hidden;
        min-width: 0;
      `}
    >
      {(title || actions) && (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: ${nd.space.sm}px;
            padding: ${nd.space.sm}px ${nd.space.md}px;
          `}
        >
          <div
            css={css`
              flex: 1 1 200px;
              min-width: 0;
              font-weight: ${nd.fontWeight.medium};
            `}
          >
            {title}
          </div>
          {actions}
        </div>
      )}
      {children && (
        <div
          css={css`
            ${padded
              ? `padding: ${title || actions ? 0 : nd.space.sm}px ${nd.space.md}px ${
                  nd.space.sm
                }px;`
              : ""}
            background: ${padded ? "transparent" : nd.color.surface};
          `}
        >
          {children}
        </div>
      )}
    </div>
  )
}

Card.Inset = CardInset

/**
 * Side by side parts of a Card (e.g. Supplier and Ship to) for a record's parties, so they sit
 * above the main table rather than down a sidebar that can outgrow the page. Each child is a
 * column with a thin rule between them; they drop onto their own rows when narrow.
 */
function CardColumns({
  children,
  minColumnWidth = 240,
}: {
  children: React.ReactNode
  /** Columns stack when they'd be narrower than this */
  minColumnWidth?: number
}) {
  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(${minColumnWidth}px, 100%), 1fr));
        gap: 1px;
        background: ${nd.color.border};
        /* clip the columns' square backgrounds to the card's corners (it holds no menus) */
        border-radius: inherit;
        overflow: hidden;
        & > * {
          min-width: 0;
          padding: ${nd.space.md}px ${nd.space.lg}px ${nd.space.lg}px;
          background: ${nd.color.surface};
        }
      `}
    >
      {children}
    </div>
  )
}

Card.Columns = CardColumns

/** One column of `Card.Columns`: a small heading with optional actions, then the content. */
function CardColumn({
  title,
  actions,
  children,
}: {
  title: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${nd.space.sm}px;
          margin-bottom: ${nd.space.sm}px;
          min-height: 20px;
        `}
      >
        <h3
          css={css`
            flex: 1;
            margin: 0;
            font-size: ${nd.fontSize.small}px;
            font-weight: ${nd.fontWeight.medium};
            color: ${nd.color.textSubdued};
          `}
        >
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </div>
  )
}

Card.Column = CardColumn
